import React, { useState, useEffect } from 'react';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, LoadingSpinner } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { reportsAPI } from '../utils/api';
import {
  BarChart3, Send, Plus, Trash2, Mail, Clock, CheckCircle2,
  XCircle, RefreshCw, Users, Target, AlertTriangle, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const STAGE_COLORS = {
  NEW: 'bg-blue-100 text-blue-700',
  CONTACTED: 'bg-sky-100 text-sky-700',
  DATA_PENDING: 'bg-amber-100 text-amber-700',
  DATA_RECEIVED: 'bg-teal-100 text-teal-700',
  CONFIGURED: 'bg-indigo-100 text-indigo-700',
  PROPOSAL_SENT: 'bg-purple-100 text-purple-700',
  WON: 'bg-green-100 text-green-700',
  LOST: 'bg-red-100 text-red-700',
};

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState('report');
  const [reportData, setReportData] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showAddSub, setShowAddSub] = useState(false);
  const [subForm, setSubForm] = useState({ recipient_email: '', recipient_name: '', schedule: 'daily' });
  const [addingSub, setAddingSub] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [reportRes, subsRes, logsRes] = await Promise.all([
        reportsAPI.getDailySales(),
        reportsAPI.getSubscriptions(),
        reportsAPI.getEmailLogs('daily_sales', 50),
      ]);
      setReportData(reportRes.data);
      setSubscriptions(subsRes.data);
      setEmailLogs(logsRes.data);
    } catch (err) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReport = async () => {
    setSending(true);
    try {
      const res = await reportsAPI.sendDailySales();
      const data = res.data;
      const sentCount = data.results?.filter(r => r.status === 'sent').length || 0;
      const failCount = data.results?.filter(r => r.status === 'failed').length || 0;
      if (failCount > 0) {
        toast.warning(`Sent: ${sentCount}, Failed: ${failCount}`);
      } else {
        toast.success(`Report sent to ${sentCount} recipient(s)`);
      }
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send report');
    } finally {
      setSending(false);
    }
  };

  const handleAddSubscription = async (e) => {
    e.preventDefault();
    setAddingSub(true);
    try {
      await reportsAPI.createSubscription({
        report_type: 'daily_sales',
        ...subForm,
      });
      toast.success('Subscriber added');
      setShowAddSub(false);
      setSubForm({ recipient_email: '', recipient_name: '', schedule: 'daily' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add subscriber');
    } finally {
      setAddingSub(false);
    }
  };

  const handleDeleteSubscription = async (subId) => {
    if (!window.confirm('Remove this subscriber?')) return;
    try {
      await reportsAPI.deleteSubscription(subId);
      toast.success('Subscriber removed');
      fetchAll();
    } catch (err) {
      toast.error('Failed to remove subscriber');
    }
  };

  const handleToggleSubscription = async (sub) => {
    try {
      await reportsAPI.updateSubscription(sub.subscription_id, { active: !sub.active });
      fetchAll();
    } catch (err) {
      toast.error('Failed to update subscriber');
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0"><LoadingSpinner /></div>
      </div>
    );
  }

  const tabs = [
    { key: 'report', label: 'Daily Sales Report', icon: BarChart3 },
    { key: 'subscribers', label: 'Subscribers', icon: Users },
    { key: 'logs', label: 'Email Logs', icon: Mail },
  ];

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="reports-page">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-8">
          <PageHeader
            title="Reports"
            description="Daily Sales Pipeline Report &mdash; view, send, and manage subscriptions"
            action={
              <div className="flex space-x-2">
                <Button variant="outline" onClick={fetchAll} data-testid="refresh-report">
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
                <Button onClick={handleSendReport} disabled={sending} className="bg-slate-900 hover:bg-slate-800" data-testid="send-report-button">
                  <Send className="w-4 h-4 mr-2" /> {sending ? 'Sending...' : 'Send Report Now'}
                </Button>
              </div>
            }
          />

          {/* Tabs */}
          <div className="flex border-b border-slate-200 mb-6 mt-2">
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                data-testid={`report-tab-${t.key}`}
                className={`flex items-center px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === t.key
                    ? 'border-slate-900 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <t.icon className="w-4 h-4 mr-2" />{t.label}
              </button>
            ))}
          </div>

          {activeTab === 'report' && reportData && <DailySalesReportView data={reportData} />}
          {activeTab === 'subscribers' && (
            <SubscribersView
              subscriptions={subscriptions}
              onAdd={() => setShowAddSub(true)}
              onDelete={handleDeleteSubscription}
              onToggle={handleToggleSubscription}
            />
          )}
          {activeTab === 'logs' && <EmailLogsView logs={emailLogs} />}
        </div>
      </div>

      {/* Add Subscriber Dialog */}
      <Dialog open={showAddSub} onOpenChange={setShowAddSub}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Report Subscriber</DialogTitle></DialogHeader>
          <form onSubmit={handleAddSubscription} className="space-y-4 mt-4">
            <div>
              <Label>Email Address</Label>
              <Input
                type="email" required
                value={subForm.recipient_email}
                onChange={e => setSubForm({ ...subForm, recipient_email: e.target.value })}
                placeholder="user@company.com"
                className="mt-1.5"
                data-testid="sub-email-input"
              />
            </div>
            <div>
              <Label>Name</Label>
              <Input
                value={subForm.recipient_name}
                onChange={e => setSubForm({ ...subForm, recipient_name: e.target.value })}
                placeholder="John Doe"
                className="mt-1.5"
                data-testid="sub-name-input"
              />
            </div>
            <div>
              <Label>Schedule</Label>
              <select
                value={subForm.schedule}
                onChange={e => setSubForm({ ...subForm, schedule: e.target.value })}
                className="w-full mt-1.5 px-3 py-2 border border-slate-200 rounded-sm text-sm"
                data-testid="sub-schedule-select"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="manual">Manual Only</option>
              </select>
            </div>
            <div className="flex justify-end space-x-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowAddSub(false)}>Cancel</Button>
              <Button type="submit" disabled={addingSub} className="bg-slate-900 hover:bg-slate-800" data-testid="add-sub-submit">
                {addingSub ? 'Adding...' : 'Add Subscriber'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DailySalesReportView({ data }) {
  const kpis = data.header_kpis || {};
  const pipeline = data.pipeline_by_stage || [];
  const actions = data.top_action_items || [];
  const exceptions = data.exceptions || [];

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPICard label="Prospects" value={kpis.total_prospects} icon={Users} color="blue" />
        <KPICard label="Pipeline Sites" value={kpis.total_pipeline_sites} icon={Target} color="slate" />
        <KPICard label="Active" value={kpis.active_sites} icon={TrendingUp} color="emerald" />
        <KPICard label="Won" value={kpis.won} icon={CheckCircle2} color="amber" />
        <KPICard label="Lost" value={kpis.lost} icon={XCircle} color="red" />
      </div>

      {/* Pipeline by Stage */}
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Pipeline by Stage</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {pipeline.map(p => (
            <div key={p.stage} className="flex items-center justify-between p-3 rounded border border-slate-100" data-testid={`stage-${p.stage}`}>
              <span className={`px-2 py-0.5 text-xs rounded-full ${STAGE_COLORS[p.stage] || 'bg-slate-100 text-slate-600'}`}>
                {p.stage.replace('_', ' ')}
              </span>
              <span className="text-lg font-bold text-slate-900">{p.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top 10 Action Items */}
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Top 10 Action Items</h3>
        {actions.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">No pending action items.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
                  <th className="pb-2 pr-3">#</th>
                  <th className="pb-2 pr-3">Company</th>
                  <th className="pb-2 pr-3">Site</th>
                  <th className="pb-2 pr-3">Stage</th>
                  <th className="pb-2 pr-3">Priority</th>
                  <th className="pb-2">Next Action</th>
                </tr>
              </thead>
              <tbody>
                {actions.map((a, i) => (
                  <tr key={a.prospect_site_id} className="border-b border-slate-100" data-testid={`action-item-${i}`}>
                    <td className="py-2.5 pr-3 text-slate-400">{i + 1}</td>
                    <td className="py-2.5 pr-3 font-medium text-slate-900">{a.company_name}</td>
                    <td className="py-2.5 pr-3">{a.site_name}</td>
                    <td className="py-2.5 pr-3">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${STAGE_COLORS[a.pipeline_stage] || 'bg-slate-100'}`}>
                        {a.pipeline_stage}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3">
                      <span className={`text-xs font-semibold ${
                        a.priority === 'HIGH' ? 'text-red-600' : a.priority === 'MED' ? 'text-amber-600' : 'text-green-600'
                      }`}>{a.priority}</span>
                    </td>
                    <td className="py-2.5 text-slate-600">{a.next_action || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Exceptions */}
      {exceptions.length > 0 && (
        <div className="bg-white border border-red-100 rounded-sm p-6">
          <h3 className="font-semibold text-red-700 mb-4 flex items-center">
            <AlertTriangle className="w-4 h-4 mr-2" /> Exceptions (Stale 7+ days)
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-red-100 text-left text-xs text-slate-500 uppercase">
                  <th className="pb-2 pr-3">Company</th>
                  <th className="pb-2 pr-3">Site</th>
                  <th className="pb-2 pr-3">Stage</th>
                  <th className="pb-2">Days Stale</th>
                </tr>
              </thead>
              <tbody>
                {exceptions.map((e, i) => (
                  <tr key={e.id || e.company_name || i} className="border-b border-red-50" data-testid={`exception-${i}`}>
                    <td className="py-2.5 pr-3 font-medium">{e.company_name}</td>
                    <td className="py-2.5 pr-3">{e.site_name}</td>
                    <td className="py-2.5 pr-3">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${STAGE_COLORS[e.pipeline_stage] || 'bg-slate-100'}`}>
                        {e.pipeline_stage}
                      </span>
                    </td>
                    <td className="py-2.5 text-red-600 font-semibold">{e.days_stale} days</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-slate-400 text-right">Report generated: {data.generated_at}</p>
    </div>
  );
}

function KPICard({ label, value, icon: Icon, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    slate: 'bg-slate-100 text-slate-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={`kpi-${label.toLowerCase()}`}>
      <div className="flex items-center space-x-3">
        <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <p className="text-xl font-bold text-slate-900">{value ?? 0}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

function SubscribersView({ subscriptions, onAdd, onDelete, onToggle }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-slate-900">Report Subscribers</h3>
        <Button onClick={onAdd} className="bg-slate-900 hover:bg-slate-800" data-testid="add-subscriber-button">
          <Plus className="w-4 h-4 mr-2" /> Add Subscriber
        </Button>
      </div>

      {subscriptions.length === 0 ? (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-sm">
          <Mail className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No subscribers configured yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {subscriptions.map(sub => (
            <div
              key={sub.subscription_id}
              className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between"
              data-testid={`subscriber-${sub.subscription_id}`}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${sub.active ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-400'}`}>
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">{sub.recipient_name || sub.recipient_email}</p>
                  <p className="text-xs text-slate-500">{sub.recipient_email} &middot; {sub.schedule}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onToggle(sub)}
                  className={`px-3 py-1 text-xs rounded border transition-colors ${
                    sub.active ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
                  }`}
                  data-testid={`toggle-sub-${sub.subscription_id}`}
                >
                  {sub.active ? 'Active' : 'Paused'}
                </button>
                <button
                  onClick={() => onDelete(sub.subscription_id)}
                  className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                  data-testid={`delete-sub-${sub.subscription_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmailLogsView({ logs }) {
  return (
    <div>
      <h3 className="font-semibold text-slate-900 mb-4">Email Delivery Logs</h3>
      {logs.length === 0 ? (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-sm">
          <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No emails sent yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
                <th className="px-4 py-3">Recipient</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Sent At</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.log_id} className="border-b border-slate-100" data-testid={`email-log-${log.log_id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900">{log.recipient_name || log.recipient_email}</p>
                    <p className="text-xs text-slate-500">{log.recipient_email}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{log.subject}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full ${
                      log.status === 'sent' ? 'bg-green-100 text-green-700' :
                      log.status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {log.status === 'sent' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                      {log.status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
                      {log.status}
                    </span>
                    {log.error_message && <p className="text-xs text-red-500 mt-0.5">{log.error_message}</p>}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{log.sent_at ? new Date(log.sent_at).toLocaleString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
